// components/features/support/auditLogs/AuditLogRow.tsx
import { useState } from "react";
import { AuditLogEntry } from "../../../../types/features/support/AuditLog";

interface AuditLogRowProps {
  log: AuditLogEntry;
}

function statusColor(status: number): string {
  if (status >= 500) return "bg-red-100 text-red-700";
  if (status >= 400) return "bg-orange-100 text-orange-700";
  if (status >= 200 && status < 300) return "bg-green-100 text-green-700";
  return "bg-gray-100 text-gray-700";
}

function methodColor(method: string): string {
  switch (method) {
    case "GET":
      return "bg-blue-100 text-blue-700";
    case "POST":
      return "bg-green-100 text-green-700";
    case "PUT":
    case "PATCH":
      return "bg-yellow-100 text-yellow-700";
    case "DELETE":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function JsonBlock({ title, data }: { title: string; data: unknown }) {
  if (data === null || data === undefined) {
    return (
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase mb-1">{title}</p>
        <p className="text-sm text-gray-400 italic">Aucune donnée</p>
      </div>
    );
  }
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase mb-1">{title}</p>
      <pre className="text-xs bg-gray-900 text-gray-100 rounded-md p-3 overflow-x-auto max-h-64 overflow-y-auto">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

export default function AuditLogRow({ log }: AuditLogRowProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        onClick={() => setExpanded((prev) => !prev)}
        className="cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <td className="py-3 px-4 text-sm text-gray-700 whitespace-nowrap">
          {formatDate(log.timestamp)}
        </td>
        <td className="py-3 px-4">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${methodColor(log.method)}`}>
            {log.method}
          </span>
        </td>
        <td className="py-3 px-4 text-sm text-gray-700 font-mono max-w-xs truncate" title={log.route}>
          {log.route}
        </td>
        <td className="py-3 px-4">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${statusColor(log.status_code)}`}>
            {log.status_code}
          </span>
        </td>
        <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">
          {log.duration_ms.toFixed(0)} ms
        </td>
        <td className="py-3 px-4 text-right">
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform inline-block ${expanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={6} className="px-4 pb-4 pt-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1 text-sm">
                <p>
                  <span className="text-gray-500">Trace ID :</span>{" "}
                  <span className="font-mono text-xs">{log.trace_id}</span>
                </p>
                <p>
                  <span className="text-gray-500">Adresse IP :</span> {log.ip || "—"}
                </p>
                <p>
                  <span className="text-gray-500">Paramètres URL :</span>{" "}
                  <span className="font-mono text-xs">
                    {log.query_params ? JSON.stringify(log.query_params) : "aucun"}
                  </span>
                </p>
                {log.exception && (
                  <p className="text-red-600">
                    <span className="font-medium">Exception :</span> {log.exception.type} —{" "}
                    {log.exception.message}
                  </p>
                )}
              </div>
              <div />
              <JsonBlock title="Données envoyées (body)" data={log.request_payload} />
              <JsonBlock title="Réponse retournée" data={log.response_payload} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
